import { useEffect, useMemo, useState } from "react";
import { Loader, Stack, Table, Text } from "@mantine/core";
import {
  fetchOrdersForPeriod,
  getOrderCreatedAt,
  getOrderDeliveredAt,
  PeriodPreset,
  StatsOrder,
  shouldCountOrder,
} from "./statsShared";
import { StatsPeriodPicker } from "./StatsPeriodPicker";

interface DriverAvgRow {
  driver: string;
  avgMinutes: number;
  deliveries: number;
}

const formatMinutes = (value: number) => {
  const rounded = Math.round(value);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  return hours > 0 ? `${hours}h ${minutes}min` : `${minutes} min`;
};

const getDriverName = (order: StatsOrder) => {
  const raw = order.DeliveredBy ?? order.deliveredBy;
  const normalized = String(raw ?? "").trim();
  return normalized || "Nepoznato";
};

export const AverageDeliveryWaitStat = () => {
  const [period, setPeriod] = useState<PeriodPreset>("today");
  const [customDate, setCustomDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avgDeliveryWaitMinutes, setAvgDeliveryWaitMinutes] = useState<
    number | null
  >(null);
  const [avgDeliverySampleSize, setAvgDeliverySampleSize] = useState(0);
  const [driverRows, setDriverRows] = useState<DriverAvgRow[]>([]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const orders = await fetchOrdersForPeriod(period, customDate);
        if (!mounted) return;

        const deliveries = orders
          .filter(shouldCountOrder)
          .filter(
            (order) =>
              !String(order.Adresa ?? "")
                .toUpperCase()
                .includes("PREUZIMANJE"),
          )
          .map((order) => {
            const createdAt = getOrderCreatedAt(order);
            const deliveredAt = getOrderDeliveredAt(order);

            if (!createdAt || !deliveredAt) return null;

            const minutes =
              (deliveredAt.getTime() - createdAt.getTime()) / 60000;
            if (!(minutes >= 0 && minutes <= 24 * 60)) return null;

            return {
              driver: getDriverName(order),
              minutes,
            };
          })
          .filter(
            (value): value is { driver: string; minutes: number } =>
              value !== null,
          );

        if (deliveries.length === 0) {
          setAvgDeliveryWaitMinutes(null);
          setAvgDeliverySampleSize(0);
          setDriverRows([]);
          return;
        }

        const totalMinutes = deliveries.reduce(
          (sum, delivery) => sum + delivery.minutes,
          0,
        );

        const byDriver = new Map<
          string,
          { totalMinutes: number; deliveries: number }
        >();

        deliveries.forEach((delivery) => {
          const current = byDriver.get(delivery.driver) ?? {
            totalMinutes: 0,
            deliveries: 0,
          };

          byDriver.set(delivery.driver, {
            totalMinutes: current.totalMinutes + delivery.minutes,
            deliveries: current.deliveries + 1,
          });
        });

        const rows: DriverAvgRow[] = Array.from(byDriver.entries()).map(
          ([driver, values]) => ({
            driver,
            deliveries: values.deliveries,
            avgMinutes: values.totalMinutes / values.deliveries,
          }),
        );

        rows.sort(
          (a, b) => b.deliveries - a.deliveries || a.avgMinutes - b.avgMinutes,
        );

        setAvgDeliveryWaitMinutes(totalMinutes / deliveries.length);
        setAvgDeliverySampleSize(deliveries.length);
        setDriverRows(rows);
      } catch (statsError) {
        if (!mounted) return;
        console.error("Error loading avg wait stats", statsError);
        setError("Greška kod učitavanja statistike.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [period, customDate]);

  const avgWaitLabel = useMemo(() => {
    if (avgDeliveryWaitMinutes === null) return "-";

    return formatMinutes(avgDeliveryWaitMinutes);
  }, [avgDeliveryWaitMinutes]);

  return (
    <Stack gap="md">
      <StatsPeriodPicker
        period={period}
        customDate={customDate}
        onPeriodChange={setPeriod}
        onCustomDateChange={setCustomDate}
      />

      {loading && <Loader variant="dots" />}

      {!loading && error && <Text color="red">{error}</Text>}

      {!loading && !error && avgDeliveryWaitMinutes !== null && (
        <>
          <Text size="lg" fw={700}>
            Prosječno čekanje: {avgWaitLabel}
          </Text>
          <Text size="sm" color="dimmed">
            Broj dostava uključenih u izračun: {avgDeliverySampleSize}
          </Text>

          {driverRows.length > 0 && (
            <Table withTableBorder withColumnBorders withRowBorders>
              <thead>
                <tr>
                  <th
                    style={{
                      border: "1px solid var(--mantine-color-gray-4)",
                      padding: "10px 12px",
                      textAlign: "left",
                    }}
                  >
                    Dostavljač
                  </th>
                  <th
                    style={{
                      border: "1px solid var(--mantine-color-gray-4)",
                      padding: "10px 12px",
                      textAlign: "left",
                    }}
                  >
                    Broj dostava
                  </th>
                  <th
                    style={{
                      border: "1px solid var(--mantine-color-gray-4)",
                      padding: "10px 12px",
                      textAlign: "left",
                    }}
                  >
                    Prosječno vrijeme
                  </th>
                </tr>
              </thead>
              <tbody>
                {driverRows.map((row, index) => (
                  <tr
                    key={row.driver}
                    style={{
                      backgroundColor:
                        index % 2 === 1
                          ? "var(--mantine-color-gray-1)"
                          : "transparent",
                    }}
                  >
                    <td
                      style={{
                        border: "1px solid var(--mantine-color-gray-4)",
                        padding: "10px 12px",
                      }}
                    >
                      {row.driver}
                    </td>
                    <td
                      style={{
                        border: "1px solid var(--mantine-color-gray-4)",
                        padding: "10px 12px",
                      }}
                    >
                      {row.deliveries}
                    </td>
                    <td
                      style={{
                        border: "1px solid var(--mantine-color-gray-4)",
                        padding: "10px 12px",
                      }}
                    >
                      {formatMinutes(row.avgMinutes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {!loading && !error && avgDeliveryWaitMinutes === null && (
        <Text color="dimmed">
          Nema dovoljno podataka za izračun u odabranom periodu.
        </Text>
      )}
    </Stack>
  );
};
